import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { garagesApi, usersApi, uploadUrl } from '../../api/client';
import type { GarageCarApi } from '../../api/client';
import type { GarageCarNavState } from '../../navigation/garageCarNav';
import { getPageTitle } from '../../utils/seo';
import styles from './UserGarage.module.css';

function getGarageTitle(displayName: string): string {
  const trimmed = displayName.trim();
  return trimmed ? `${trimmed}'s Garage` : 'Garage';
}

function GarageCard({ car, state, showPastBadge = false }: { car: GarageCarApi; state: GarageCarNavState; showPastBadge?: boolean }) {
  const likeCount = car.likes?.length ?? 0;
  const commentCount = car.comments?.length ?? 0;

  return (
    <Link to={`/garage/${car.id}`} state={state} className={styles.card}>
      <div className={styles.cardImage}>
        {car.photos?.[0] ? (
          <img src={car.photos[0].startsWith('http') ? car.photos[0] : uploadUrl(car.photos[0])} alt="" />
        ) : (
          <span className={styles.placeholder}>🚗</span>
        )}
        {car.isForSale && <span className={styles.forSaleBanner}>For Sale</span>}
      </div>
      <div className={styles.cardBody}>
        <span className={styles.cardTitle}>
          {car.car?.year} {car.car?.make} {car.car?.model}
        </span>
        {car.name && <span className={styles.cardName}>{car.name}</span>}
        <div className={styles.cardMeta}>
          <span>♥ {likeCount}</span>
          <span>💬 {commentCount}</span>
          {showPastBadge && <span className={styles.pastBadge}>Past</span>}
        </div>
      </div>
    </Link>
  );
}

export function UserGarage() {
  const { userId, username } = useParams<{ userId?: string; username?: string }>();
  const [user, setUser] = useState<{ id: string; username: string; displayName: string; avatarUrl?: string } | null>(null);
  const [cars, setCars] = useState<GarageCarApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const byId = userId && userId.trim().length > 0 ? userId : undefined;
    const byUsernameRaw = !byId && username && username.trim().length > 0 ? username : undefined;
    const byUsername =
      byUsernameRaw && byUsernameRaw.trim().length > 0
        ? byUsernameRaw.startsWith('@')
          ? byUsernameRaw.slice(1)
          : byUsernameRaw
        : undefined;
    if (!byId && !byUsername) return;
    setLoading(true);
    setError(null);
    try {
      const userRes = byId ? await usersApi.get(byId) : await usersApi.getByUsername(byUsername!);
      const carsRes = await garagesApi.getByUser(userRes.id);
      setUser(userRes);
      setCars(carsRes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load garage');
      setUser(null);
      setCars([]);
    } finally {
      setLoading(false);
    }
  }, [userId, username]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!user) return;

    const title = getGarageTitle(user.displayName);
    document.title = getPageTitle(title);
  }, [user]);

  if (!userId && !username) {
    return (
      <div className={styles.page}>
        <p>Invalid user.</p>
        <Link to="/garages/featured">Back to Featured Garages</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <p className={styles.loading}>Loading…</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className={styles.page}>
        <p className={styles.error}>{error ?? 'User not found.'}</p>
        <Link to="/garages/featured">Back to Featured Garages</Link>
      </div>
    );
  }

  const currentCars = cars.filter((c) => !c.isPastCar);
  const pastCars = cars.filter((c) => c.isPastCar);
  const garageLinkState: GarageCarNavState = { garageBack: `/garages/user/${user.id}` };

  return (
    <div className={styles.page}>
      <Link to="/garages/featured" className={styles.back}>← Featured Garages</Link>

      <div className={styles.profile}>
        <div className={styles.avatar}>
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl.startsWith('http') ? user.avatarUrl : uploadUrl(user.avatarUrl)}
              alt=""
              className={styles.avatarImg}
            />
          ) : (
            <span className={styles.avatarLetter}>{user.displayName.charAt(0)}</span>
          )}
        </div>
        <h1>{getGarageTitle(user.displayName)}</h1>
        <p className={styles.username}>@{user.username}</p>
      </div>

      {cars.length === 0 ? (
        <p className={styles.empty}>No cars in this garage yet.</p>
      ) : (
        <>
          {currentCars.length > 0 && (
            <section className={styles.section}>
              <h2>Current</h2>
              <div className={styles.grid}>
                {currentCars.map((car) => (
                  <GarageCard key={car.id} car={car} state={garageLinkState} />
                ))}
              </div>
            </section>
          )}

          {pastCars.length > 0 && (
            <section className={styles.section}>
              <h2>Past cars</h2>
              <div className={styles.grid}>
                {pastCars.map((car) => (
                  <GarageCard key={car.id} car={car} state={garageLinkState} showPastBadge />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
